import { Box, Button, Paper } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { Fragment, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useQsFilters } from "../hooks/filters"
import { Donatore, Fondo } from "../types"
import { Handshake } from "@mui/icons-material"
import { useSnackbarMessage } from "../hooks/snackbar"
import DialogConfirm from "../components/DialogConfirm"
import { useDonatoreCreate, useDonatoreDelete, useDonatoreUpdate, useDonatoriList } from "../hooks/donatori"
import DonatoreForm from "../components/forms/DonatoreForm"
import { useNavigate } from "react-router-dom"
import IconButtonDelete from "../components/IconButtonDelete"
import IconButtonEdit from "../components/IconButtonEdit"
import useUserCanWriteAndDelete from "../hooks/permissions"
import LangLink from "../components/LangLink"
import { useIsMobile } from "../hooks/mobile"
import { useIsTablet } from "../hooks/tablet"
import { useAuth } from "../auth"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
})

export default function Donatori() {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const canWrite = useUserCanWriteAndDelete()

  const { user } = useAuth()
  const isMobile = useIsMobile() || user?.display === "mobile"
  const isTablet = (useIsTablet() || user?.display === "tablet") && !isMobile

  const columns: GridColDef[] = useMemo(() => {
    if (isMobile) {
      return [
        {
          field: "nome",
          headerName: t("donatore.nome"),
          flex: 1,
          renderCell: (params) => (
            <LangLink style={{ color: "black" }} to={`/donatori/${params.row.id}`}>
              {params.row.cognome}, {params.row.nome}
            </LangLink>
          ),
        },
      ]
    }
    if (isTablet) {
      return [
        {
          field: "cognome",
          headerName: t("donatore.cognome"),
          flex: 1,
          renderCell: (params) => (
            <LangLink style={{ color: "black" }} to={`/donatori/${params.row.id}`}>
              {params.row.cognome}
            </LangLink>
          ),
        },
        {
          field: "nome",
          headerName: t("donatore.nome"),
          flex: 1,
          renderCell: (params) => (
            <LangLink style={{ color: "black" }} to={`/donatori/${params.row.id}`}>
              {params.row.nome}
            </LangLink>
          ),
        },
        {
          field: "id",
          headerName: t("general.azioni"),
          flex: 1,
          display: "flex",
          align: "right",
          headerAlign: "right",
          hideSortIcons: true,
          sortable: false,
          renderCell: (params) => (
            <>
              <IconButtonEdit onClick={() => editModalActions.open(params.row)} />
              {canWrite && (
                <IconButtonDelete
                  onClick={() => deleteModalActions.open(params.row)}
                  disabled={!params.row.can_delete}
                />
              )}
            </>
          ),
        },
      ]
    }
    return [
      {
        field: "cognome",
        headerName: t("donatore.cognome"),
        flex: 1,
        renderCell: (params) => (
          <LangLink style={{ color: "black" }} to={`/donatori/${params.row.id}`}>
            {params.row.cognome}
          </LangLink>
        ),
      },
      {
        field: "nome",
        headerName: t("donatore.nome"),
        flex: 1,
        renderCell: (params) => (
          <LangLink style={{ color: "black" }} to={`/donatori/${params.row.id}`}>
            {params.row.nome}
          </LangLink>
        ),
      },
      {
        field: "telefono",
        headerName: t("donatore.telefono"),
        flex: 1,
      },
      {
        field: "email",
        headerName: t("donatore.email"),
        flex: 1,
      },
      {
        field: "fondi_data",
        headerName: t("donatore.fondi"),
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          const fondi = params.row.fondi_data || ([] as Fondo[])
          return (
            <Box>
              {fondi.map((fondo: Fondo) => (
                <Fragment key={fondo.id}>
                  <LangLink key={fondo.id} style={{ color: "black" }} to={`/fondi/${fondo.id}`}>
                    {fondo.segnatura}
                  </LangLink>
                  {fondo !== fondi[fondi.length - 1] && ", "}
                </Fragment>
              ))}
            </Box>
          )
        },
      },

      {
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButtonEdit onClick={() => editModalActions.open(params.row)} />
            {canWrite && (
              <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} disabled={!params.row.can_delete} />
            )}
          </>
        ),
      },
    ]
  }, [t, canWrite, isMobile, isTablet])

  const { data, isLoading } = useDonatoriList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
  })

  const { mutateAsync: create } = useDonatoreCreate()
  const { mutateAsync: update } = useDonatoreUpdate()
  const { mutateAsync: remove } = useDonatoreDelete()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0

    return count
  }, [])

  const lang = i18n.language

  useEffect(() => {
    if (user?.ruolo === "consultazione") {
      navigate(`/${lang}/fondi`)
    }
  }, [user, lang, navigate])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("donatore.titolo_lista")}
            breadcrumbs={[
              {
                label: t("donatore.donatori"),
                iconType: Handshake,
              },
            ]}
          />
          {canWrite && !isMobile && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("donatore.cta_aggiungi")}
            </Button>
          )}
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1 })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            />
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Donatore>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("donatore.cta_aggiungi")}
        FormComponent={DonatoreForm}
        save={(values: Donatore) => {
          return create(values).then((response) => {
            createModalActions.close()
            showMessage(t("donatore.esito_crea_ok"))
            navigate(`/${i18n.language}/donatori/${response.id}`)
          })
        }}
      />
      <DialogForm<Donatore>
        open={editModalState.isOpen}
        hasCancel
        handleClose={editModalActions.close}
        title={t("donatore.cta_modifica")}
        FormComponent={DonatoreForm}
        save={(values: Donatore) => {
          return update(values).then(() => {
            editModalActions.close()
            showMessage(t("donatore.esito_modifica_ok"))
          })
        }}
        item={editModalState.value}
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("donatore.cta_elimina")}
        subtitle={
          <span>
            {t("donatore.cta_subtitle_elimina")} <b>{deleteModalState.value?.nome}</b> ?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("donatore.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
